import type {
  EnhancedPOIsResponse,
  EnhancedPOIs,
  POI,
} from "@local-logic/client";
import type { POIList } from "../types";

/**
 * Enhanced POIs response type has a very different structure compared to v3/pois and v3/schools.
 * Since we're still using v3/schools along v3/enhanced-pois, we need to normalize the enhanced POIs response
 * to match the structure of the POIs response in order to easily merge them together when we want to display them
 * on a map or a list.
 */
export function normalizeEnhancedPoisToPois(
  categories?: EnhancedPOIs[],
  enhancedPoisResponse?: EnhancedPOIsResponse,
): POIList | undefined {
  if (
    typeof categories === "undefined" ||
    categories.length === 0 ||
    // Even though the API should always return an array for results, we add this safeguard just in case
    typeof enhancedPoisResponse?.data?.results === "undefined"
  ) {
    return undefined;
  }

  const data = enhancedPoisResponse.data.results;
  const result: POIList = {} as POIList;

  categories.forEach((category) => {
    result[category] = data
      .filter((poi) => poi.categories.includes(category))
      .map(
        (poi) =>
          ({
            name: poi.name,
            address: poi.address,
            city: poi.city,
            province: poi.province,
            postalcode: poi.postal_code,
            country: poi.country,
            lat: poi.lat,
            lng: poi.lng,
            url: poi.url,
            proximity: {
              linear_distance: poi.proximity.linear_distance_m,
              walking: poi.proximity.walking,
            },
            routes: poi.routes,
          }) as POI,
      );
  });

  return result;
}
